import { RecipeCard } from "@/components/recipes/RecipeCard";
import { RecipeCardList } from "@/components/recipes/RecipeCardList";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { canAddRecipes, canDeleteRecipe, canEditRecipe } from "@/lib/recipe-box-permissions";
import type { Recipe, RecipeBox, ViewPreference } from "@/lib/types";
import { Copy, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { NewRecipeTile } from "./NewRecipeTile";

const recipeSkeletonKeys = ["one", "two", "three", "four"] as const;

export type RecipesSectionProps = Readonly<{
  recipes: Recipe[];
  recipeBox: RecipeBox | null;
  userId: string | null;
  recipeBoxId: string;
  loading: boolean;
  error: string | null;
  viewPreference: ViewPreference;
  onRequestDelete: (recipe: Recipe) => void;
  onRequestClone?: (recipe: Recipe) => void;
}>;

export const RecipesSection = ({
  recipes,
  recipeBox,
  userId,
  recipeBoxId,
  loading,
  error,
  viewPreference,
  onRequestDelete,
  onRequestClone,
}: RecipesSectionProps) => {
  return (
    <>
      {error ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p>We couldn&apos;t load recipes for this box. Please try again.</p>
        </div>
      ) : null}

      {loading ? (
        viewPreference === "card" ? (
          <div className="grid gap-3 w-full max-w-full" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(9rem, 1fr))" }}>
            {recipeSkeletonKeys.map((key) => (
              <Skeleton key={key} className="aspect-square w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="space-y-3 w-full max-w-full">
            {recipeSkeletonKeys.map((key) => (
              <Skeleton key={key} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        )
      ) : null}

      {!loading && !error ? (
        viewPreference === "card" ? (
          <div className="grid gap-3 w-full max-w-full" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(9rem, 1fr))" }}>
            <NewRecipeTile recipeBoxId={recipeBoxId} />
            {recipes.map((recipe) => {
              const canEdit = recipeBox && userId ? canEditRecipe(recipeBox, recipe, userId) : false;
              const canDelete = recipeBox && userId ? canDeleteRecipe(recipeBox, userId) : false;
              const canClone = recipeBox && userId ? canAddRecipes(recipeBox, userId) : false;
              const showActions = canEdit || canDelete || canClone;

              return (
                <div key={recipe.id} className="group relative min-w-0 w-full">
                  <RecipeCard recipe={recipe} userId={userId} recipeBox={recipeBox} />
                  {showActions ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="secondary"
                          size="icon"
                          className="absolute right-1.5 top-1.5 h-7 w-7 rounded-full bg-background/80 backdrop-blur transition-opacity duration-200 hover:bg-background"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          aria-label={`Recipe actions for ${recipe.title}`}
                        >
                          <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        {canEdit ? (
                          <DropdownMenuItem asChild>
                            <Link to={`/recipe/${recipe.id}?mode=edit`} className="flex items-center gap-2">
                              <Pencil className="h-4 w-4" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                        ) : null}
                        {canClone && onRequestClone ? (
                          <DropdownMenuItem
                            className="flex items-center gap-2"
                            onSelect={(event) => {
                              event.preventDefault();
                              onRequestClone(recipe);
                            }}
                          >
                            <Copy className="h-4 w-4" />
                            Clone
                          </DropdownMenuItem>
                        ) : null}
                        {canDelete ? (
                          <DropdownMenuItem
                            className="flex items-center gap-2 text-destructive focus:text-destructive"
                            onSelect={(event) => {
                              event.preventDefault();
                              onRequestDelete(recipe);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3 w-full max-w-full">
            <NewRecipeTile recipeBoxId={recipeBoxId} />
            {recipes.map((recipe) => {
              const canEdit = recipeBox && userId ? canEditRecipe(recipeBox, recipe, userId) : false;
              const canDelete = recipeBox && userId ? canDeleteRecipe(recipeBox, userId) : false;
              const canClone = recipeBox && userId ? canAddRecipes(recipeBox, userId) : false;
              const showActions = canEdit || canDelete || canClone;

              return (
                <div key={recipe.id} className="group relative">
                  <RecipeCardList recipe={recipe} userId={userId} recipeBox={recipeBox} />
                  {showActions ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="secondary"
                          size="icon"
                          className="absolute right-3 top-3 h-8 w-8 rounded-full bg-background/80 backdrop-blur transition-opacity duration-200 hover:bg-background"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          aria-label={`Recipe actions for ${recipe.title}`}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        {canEdit ? (
                          <DropdownMenuItem asChild>
                            <Link to={`/recipe/${recipe.id}?mode=edit`} className="flex items-center gap-2">
                              <Pencil className="h-4 w-4" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                        ) : null}
                        {canClone && onRequestClone ? (
                          <DropdownMenuItem
                            className="flex items-center gap-2"
                            onSelect={(event) => {
                              event.preventDefault();
                              onRequestClone(recipe);
                            }}
                          >
                            <Copy className="h-4 w-4" />
                            Clone
                          </DropdownMenuItem>
                        ) : null}
                        {canDelete ? (
                          <DropdownMenuItem
                            className="flex items-center gap-2 text-destructive focus:text-destructive"
                            onSelect={(event) => {
                              event.preventDefault();
                              onRequestDelete(recipe);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
              );
            })}
          </div>
        )
      ) : null}

      {!loading && recipes.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="font-headline text-lg">No recipes yet.</p>
          <p className="mt-2 text-sm text-muted-foreground">Add your first recipe to this box and it will appear here.</p>
        </div>
      ) : null}
    </>
  );
};

